/**
 * Статус задачи для каталога.
 *
 * Значок сопровождается текстом всегда: цвет и форма не могут быть
 * единственным носителем смысла (docs/10-ui-ux.md, доступность).
 */

import type { TaskProgress } from '@/api/catalog'
import { strings } from '@/lib/strings'

export type TaskStatus = 'review' | 'solved' | 'solved-with-hints' | 'attempted' | 'not-started'

export interface StatusView {
  status: TaskStatus
  icon: string
  label: string
  /** Класс цвета значка: токен темы, не hex (docs/12-development.md). */
  className: string
}

export function taskStatus(progress: TaskProgress | null): TaskStatus {
  if (progress === null) return 'not-started'
  // Долг важнее отметки о решении: задача решена, но ждёт самостоятельного
  // решения, и именно это сейчас нужно человеку от списка (FR-P8).
  if (progress.needs_review) return 'review'
  if (progress.status === 'solved') {
    // «Решено самостоятельно» — производное свойство, отдельного флага нет
    // (docs/05-data-model.md, инвариант о solved_without_hints).
    return progress.hints_revealed === 0 && !progress.solution_revealed
      ? 'solved'
      : 'solved-with-hints'
  }
  return 'attempted'
}

export function statusView(progress: TaskProgress | null): StatusView {
  const status = taskStatus(progress)
  switch (status) {
    // Приглашение, а не ошибка: `warning` здесь читался бы как «что-то не так».
    case 'review':
      return {
        status,
        icon: '↻',
        label: strings.catalog.status.review,
        className: 'text-accent',
      }
    case 'solved':
      return { status, icon: '✓', label: strings.catalog.status.solved, className: 'text-success' }
    case 'solved-with-hints':
      return {
        status,
        icon: '✓',
        label: strings.catalog.status.solvedWithHints,
        className: 'text-success',
      }
    case 'attempted':
      return {
        status,
        icon: '◐',
        label: strings.catalog.status.attempted,
        className: 'text-warning',
      }
    default:
      return {
        status,
        icon: '○',
        label: strings.catalog.status.notStarted,
        className: 'text-muted',
      }
  }
}
